import { Flex, Typography } from "antd";
import styled from "styled-components";
import { CaretDownIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslate } from "hooks/useTranslate";
import { Slide } from "pages/media/types";
import { TemplateState } from "../../storyboardEditor/types";
import { Collapsible } from "../../storyboardEditor/components/leftPanel/Collapsible";
import { LayersPanel } from "../../storyboardEditor/components/leftPanel/LayersPanel";
import { StaticSceneRow } from "../../storyboardEditor/components/leftPanel/SceneRow";
import { SlideTemplatePreview } from "../../storyboardEditor/components/Template/SlideTemplatePreview";

const I18N = "media.themeEditor";

const COMPACT_THUMB_HEIGHT = 40;
const THUMBNAIL_BORDER = {
  borderWidth: 1,
  borderRadius: 0,
  borderColor: "#00000033",
};

const compactThumbSize = (aspectRatio: number | undefined) => {
  const ratio = aspectRatio ?? 9 / 16;
  return {
    height: COMPACT_THUMB_HEIGHT,
    width: Math.max(20, Math.round(COMPACT_THUMB_HEIGHT * ratio)),
  };
};

type ThemeLeftPanelProps = {
  slides: Slide[];
  templates: Map<string, TemplateState>;
  currentSlide: string | undefined;
  mediaAspectRatio: number;
  /** Switches the edited slide type (mirrors the storyboard scene selection). */
  onSelectSlide: (slideType: string) => void;
};

/**
 * Theme-editor left panel mirroring the storyboard editor's Scenes + Layers
 * panel. The top section lists the theme's slide types (rendered like scenes
 * but without drag/delete); the bottom section reuses the same Layers panel,
 * showing the items of the selected slide type. Selecting a layer here opens
 * the matching settings tab, replacing the old per-item "Position" controls.
 */
export const ThemeLeftPanel = ({
  slides,
  templates,
  currentSlide,
  mediaAspectRatio,
  onSelectSlide,
}: ThemeLeftPanelProps) => {
  const t = useTranslate();
  const [slidesOpen, setSlidesOpen] = useState(true);
  const [layersOpen, setLayersOpen] = useState(true);
  const thumbSize = compactThumbSize(mediaAspectRatio);

  return (
    <Container vertical>
      {/* Slide types — grows to fill and scrolls on overflow. */}
      <Flex
        vertical
        style={{ flex: slidesOpen ? "1 1 0" : "0 0 auto", minHeight: 0 }}
      >
        <SectionHeader onClick={() => setSlidesOpen((v) => !v)}>
          <SectionHeaderContent label={t(`${I18N}.slideTypes`)} open={slidesOpen} />
        </SectionHeader>
        <Collapsible open={slidesOpen} fill>
          <ScrollArea>
            <Flex vertical style={{ width: "100%" }}>
              {slides.map((slide) => {
                const template = templates.get(slide.slide_type);
                if (!template) return null;
                return (
                  <StaticSceneRow
                    key={slide.slide_type}
                    label={slide.slide_type.replaceAll("_", " ")}
                    selected={
                      !!currentSlide && currentSlide === slide.slide_type
                    }
                    onSelect={() => onSelectSlide(slide.slide_type)}
                    thumbnail={
                      <SlideTemplatePreview
                        slide={slide}
                        template={template}
                        opacity={1}
                        templateSize={thumbSize}
                        border={THUMBNAIL_BORDER}
                        onCardClick={() => onSelectSlide(slide.slide_type)}
                        marginBlockEnd={0}
                      />
                    }
                  />
                );
              })}
            </Flex>
          </ScrollArea>
        </Collapsible>
      </Flex>

      {/* Layers — capped so it never hides the slide-types list. */}
      <Flex vertical style={{ flex: "0 1 auto", minHeight: 0, maxHeight: "50%" }}>
        <SectionHeader onClick={() => setLayersOpen((v) => !v)}>
          <SectionHeaderContent label={t(`${I18N}.layers`)} open={layersOpen} />
        </SectionHeader>
        <Collapsible open={layersOpen} fill>
          <ScrollArea>
            <LayersPanel />
          </ScrollArea>
        </Collapsible>
      </Flex>
    </Container>
  );
};

const SectionHeaderContent = ({
  label,
  open,
}: {
  label: string;
  open: boolean;
}) => (
  <>
    <Caret size={14} $open={open} />
    <Typography.Text strong style={{ textTransform: "uppercase", fontSize: 13 }}>
      {label}
    </Typography.Text>
  </>
);

// ─── Styled components ───

const Container = styled(Flex)`
  height: 100%;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colorBgContainer};
`;

// Matches the right-menu tab-button tone, like the storyboard left panel.
const SectionHeader = styled(Flex)`
  cursor: pointer;
  user-select: none;
  padding: 12px;
  gap: 8px;
  align-items: center;
  background-color: ${({ theme }) => theme.colorBgContainerDisabled};
  border-block-end: 1px solid ${({ theme }) => theme.colorBorder};
`;

const Caret = styled(CaretDownIcon)<{ $open: boolean }>`
  transition: transform 0.2s ease;
  transform: rotate(${({ $open }) => ($open ? 0 : -90)}deg);
  flex-shrink: 0;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colorBorder} transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colorBorder};
    border-radius: 3px;
  }
`;
